/**
 * `mod.prefs` — a view's own small, private, DURABLE state.
 *
 * THE HOLE THIS FILLS. An app frame is `sandbox="allow-scripts"` with no
 * `allow-same-origin` (DocumentView.tsx), so its origin is opaque and every
 * `localStorage` access throws. The bridge shim therefore installs an in-memory
 * polyfill over `localStorage`, which stops views blanking — and turns every
 * write into a value that dies with the frame. It does not fail; it forgets,
 * silently, which is worse. The Review canvas shipped a fold-your-comments
 * feature that way: the caret worked all session and every fold was gone on
 * reopen, with nothing anywhere reporting a problem.
 *
 * The shim's advice was "real state belongs in mod.type.*", and for most state
 * that is right. It is wrong for this kind. Which comment threads I have folded
 * away is a READING POSITION: mine, this browser's, and no business of the
 * person reviewing the same branch beside me. Putting it on the synced record
 * would fold their diff where I folded mine.
 *
 * So the host keeps it, in the host's OWN localStorage — a real origin, where
 * storage works — on the app's behalf.
 *
 * NAMESPACED BY dataType, not by the view's say-so. The host bakes the app's
 * type in at mount and ignores anything the frame passes, so one app cannot
 * name another's namespace and read its state. Within its own namespace an app
 * chooses its keys freely; that is its business.
 *
 * NO GRANT. There is nothing to gate: this reaches no project document, no
 * session, no repo. An app writing its own scratch space and reading it back
 * gains no access it did not already have, and requiring a declaration would
 * mean every app that wants to remember a toggle files for a permission.
 */

import type { BridgeContext } from './capability'

/** One key for everything, parsed on read. Views keep a handful of small values
 *  here; a key per app per pref would be a hundred entries a person meets in
 *  their devtools and cannot interpret. */
const KEY = 'mod.app.prefs.v1'

/**
 * How much one app may keep. Not a security boundary — it shares an origin
 * with the host either way — but a bound: localStorage is a few megabytes for
 * the WHOLE origin, and a view that grows an unbounded cache in here would
 * eventually take the host's own storage down with it. Refused loudly rather
 * than trimmed, so a view that outgrows this learns it instead of losing the
 * tail of its state.
 */
const MAX_BYTES = 64 * 1024

type Store = Record<string, Record<string, unknown>>

/** This is somebody's browser storage: it can hold anything, including what an
 *  older version of this file wrote. Never throw into a view. */
function read(): Store {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Store)
      : {}
  } catch {
    return {}
  }
}

function write(store: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store))
  } catch {
    // Quota, or storage turned off mid-session. The value is lost and the view
    // keeps working, which is the same bargain every preference makes.
  }
}

/**
 * Answer `mod.prefs.*`. Three methods and no more: this is a place to put a
 * toggle, not a database. A view that wants querying wants a document.
 */
export async function prefsHandler(
  method: string,
  args: unknown[],
  ctx: BridgeContext,
): Promise<unknown> {
  const app = ctx.dataType || 'unknown'
  const verb = method.slice(method.indexOf('.') + 1)
  const key = String(args[0] ?? '')
  if (!key) throw new Error('mod.prefs: a key is required')

  if (verb === 'get') {
    // `null` for absent, and it must stay distinguishable from a stored `null`
    // in the caller's mind: absent means "never set", which is the state a
    // default belongs to.
    const value = read()[app]?.[key]
    return value === undefined ? null : value
  }

  if (verb === 'set') {
    const store = read()
    const mine = { ...(store[app] ?? {}) }
    // `undefined` does not survive JSON, so setting a value away is a delete.
    // Storing "the absence of a value" as a key is how a prefs blob grows
    // forever with things nobody set.
    if (args[1] === undefined || args[1] === null) delete mine[key]
    else mine[key] = args[1]
    const next: Store = { ...store, [app]: mine }
    if (Object.keys(mine).length === 0) delete next[app]
    const size = JSON.stringify(next[app] ?? {}).length
    if (size > MAX_BYTES) {
      throw new Error(
        `mod.prefs: ${app} is over its ${Math.round(MAX_BYTES / 1024)}KB budget (${size} bytes). Keep large state in a document.`,
      )
    }
    write(next)
    return true
  }

  if (verb === 'remove') {
    const store = read()
    if (!store[app]) return true
    const mine = { ...store[app] }
    delete mine[key]
    const next: Store = { ...store, [app]: mine }
    if (Object.keys(mine).length === 0) delete next[app]
    write(next)
    return true
  }

  throw new Error(`unknown mod.prefs method: ${verb}`)
}
